import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;

    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;

    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const parts = c.trim().split('=');
          const name = parts[0];
          const rest = parts.slice(1);

          return {
            name: name.trim(),
            value: decodeURIComponent(rest.join('=')),
          };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({
        name: k.slice(PFX.length),
        value: localStorage.getItem(k) || '',
      }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let cookie = `${name}=${value}; Path=${options?.path || '/'}`;

  if (window.location.hostname === 'localhost') {
    cookie += '; SameSite=Lax';
  } else {
    cookie += '; SameSite=None; Secure';
  }

  if (options?.maxAge) {
    cookie += `; Max-Age=${options.maxAge}`;
  }

  if (options?.expires) {
    cookie += `; Expires=${new Date(options.expires).toUTCString()}`;
  }

  document.cookie = cookie;
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host}` : ''].filter(Boolean);

  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];

  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;

    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage()).find((c) =>
    c.name.includes('auth-token')
  )?.value ?? null;

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;

  const orig = window.fetch.bind(window);

  window.fetch = (input, init) => {
    const token = getToken();

    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;

    if (
      token &&
      (url.startsWith('/') || url.startsWith(window.location.origin))
    ) {
      init = {
        ...(init || {}),
        headers: {
          ...(init?.headers || {}),
          'x-sb-token': token,
        },
      };
    }

    return orig(input, init);
  };
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),

        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;

          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value
                ? setCookie(name, value, options)
                : deleteCookie(name)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}

              if (value) {
                setCookie(name, value, options);
              }
            });
          }
        },
      },
    }
  );
}
